import Footer from "../Footer/Footer";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useI18n } from "../../../i18n/I18nContext";

export default function Consultoria() {
  const { t } = useI18n();

  const services = [
    {
      title: t("Estudo e análise do espaço disponível"),
      desc: t("Avaliação detalhada das dimensões, fluxos e condicionantes do espaço para identificar o potencial real de otimização."),
    },
    {
      title: t("Planeamento de layouts logísticos"),
      desc: t("Desenvolvimento de layouts funcionais que facilitam a circulação, o picking e a gestão de inventário."),
    },
    {
      title: t("Dimensionamento de capacidade"),
      desc: t("Cálculo rigoroso da capacidade de armazenamento necessária, com margens de crescimento futuro."),
    },
    {
      title: t("Escolha do sistema mais adequado"),
      desc: t("Seleção criteriosa do tipo de rack e acessórios com base no produto, rotação e equipamentos de movimentação."),
    },
    {
      title: t("Projetos para novos armazéns"),
      desc: t("Apoio desde a fase de projeto até à implementação, garantindo uma instalação eficiente desde o primeiro dia."),
    },
    {
      title: t("Reorganização e expansão de instalações"),
      desc: t("Análise das instalações existentes e proposta de melhorias sem necessidade de obra ou expansão física."),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <Helmet>
        <title>{t("Consultoria em Armazenagem | Racksforall")}</title>
        <meta name="description" content={t("Serviço de consultoria especializada para dimensionar a solução de armazenagem ideal para o teu armazém ou espaço profissional.")} />
        <link rel="canonical" href="https://racksforall.pt/services/consulting" />
        <meta property="og:title" content={t("Consultoria em Armazenagem | Racksforall")} />
        <meta property="og:url" content="https://racksforall.pt/services/consulting" />
      </Helmet>
      <div className="pt-20">
        {/* Hero */}
        <section className="relative overflow-hidden bg-indigo-600 text-white">
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, transparent, transparent 39px, white 39px, white 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, white 39px, white 40px)",
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none">
            <svg
              viewBox="0 0 1440 60"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-16 fill-white"
            >
              <path d="M0,40 C360,80 1080,0 1440,40 L1440,60 L0,60 Z" />
            </svg>
          </div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-24 lg:py-32 pb-28 text-center">
            <span className="inline-block mb-5 rounded-full border border-white/30 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
              {t("Serviços")}
            </span>

            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-6">
              {t("Consultoria Técnica")}
              <br />
              <span className="text-indigo-200">{t("de Armazenamento e Logística")}</span>
            </h1>

            <p className="text-lg text-indigo-100 leading-relaxed max-w-2xl mx-auto mb-10">
              {t("Mais do que vender estruturas, desenvolvemos soluções. Com mais de 20 anos de experiência, ajudamos a transformar espaço.")}
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-white text-indigo-600 px-6 py-3 text-sm font-semibold shadow hover:bg-indigo-50 transition"
              >
                {t("Falar com um consultor")}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>

              <Link
                to="/products"
                className="inline-flex items-center gap-2 rounded-xl border border-white/30 text-white px-6 py-3 text-sm font-medium hover:bg-white/10 transition"
              >
                {t("Ver produtos")}
              </Link>
            </div>
          </div>
        </section>

        {/* Introdução */}
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500 mb-3">
                {t("A nossa abordagem")}
              </p>

              <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-6">
                {t("Soluções à medida, não produtos standard")}
              </h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                {t("A nossa consultoria técnica ajuda empresas a melhorar a organização dos seus espaços e aumentar a capacidade de armazenamento sem necessidade de expansão física.")}
              </p>

              <p className="text-base text-gray-600 leading-relaxed">
                {t("Cada armazém é único — analisamos o seu espaço, os seus produtos e os seus fluxos para propor a solução mais eficiente e económica para o seu contexto específico.")}
              </p>

            </div>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100 bg-indigo-50">
                <p className="text-sm font-semibold text-indigo-700">
                  {t("Como funciona a consultoria")}
                </p>
              </div>

              <ol className="divide-y divide-gray-100">
                {[
                  {
                    step: "01",
                    title: t("Reunião inicial"),
                    desc: t("Compreendemos os objetivos, desafios e necessidades específicas da sua operação."),
                  },
                  {
                    step: "02",
                    title: t("Visita técnica"),
                    desc: t("Análise in-loco do espaço, fluxos, produtos e equipamentos existentes."),
                  },
                  {
                    step: "03",
                    title: t("Proposta de solução"),
                    desc: t("Apresentação de um plano detalhado com layout, sistema sugerido e estimativa de ganhos."),
                  },
                  {
                    step: "04",
                    title: t("Acompanhamento"),
                    desc: t("Suporte na implementação e ajustes conforme a evolução do projeto."),
                  },
                ].map((item) => (
                  <li key={item.step} className="flex gap-5 px-6 py-5 items-start">
                    <span className="shrink-0 w-8 h-8 rounded-lg bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                      {item.step}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                      <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="bg-white border-y border-gray-100">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-20">
            <div className="mb-12">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500 mb-3">
                {t("Apoiamos em")}
              </p>
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                {t("Áreas de consultoria")}
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((s, i) => (
                <div
                  key={s.title}
                  className="bg-white border border-gray-300 rounded-2xl px-8 py-8 group hover:bg-indigo-50 hover:border-indigo-200 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-bold text-indigo-400 tabular-nums group-hover:text-indigo-600 transition-colors">
                      0{i + 1}
                    </span>
                    <span className="h-px flex-1 bg-gray-200" />
                  </div>

                  <h3 className="text-base font-semibold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-20">
          <div className="rounded-2xl bg-indigo-600 px-8 py-12 sm:px-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {t("Pronto para otimizar o seu armazém?")}
              </h2>
              <p className="text-indigo-200 text-sm max-w-md leading-relaxed">
                {t("Fale connosco e descubra como podemos ajudar a transformar o espaço que já tem em algo muito mais eficiente.")}
              </p>
            </div>

            <div className="flex flex-wrap gap-3 shrink-0">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-white text-indigo-600 px-6 py-3 text-sm font-semibold hover:bg-indigo-50 transition whitespace-nowrap"
              >
                {t("Contactar agora")}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>

              <Link
                to="/products"
                className="inline-flex items-center rounded-xl border border-white/30 text-white px-6 py-3 text-sm font-medium hover:bg-white/10 transition whitespace-nowrap"
              >
                {t("Ver catálogo")}
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
